/* eslint-disable no-empty */
import { useMemo, useState } from 'react';

export type BaseFilterType = {
	[key: string]: unknown;
};

/**
 *  @example "status": { "trigger": "Status", "labels": {"draft": "Draft", "active": "Active"} }
 */
export type FriendlyFilterMap = {
	[key: string]: {
		trigger: string;
		labels: {
			[key: string]: string;
		}[];
	};
};

/**
 * Hook matching `useLocalStorage`'s shape so web/mobile can inject their own persistence.
 * When `key` is `undefined`, the value should live in React state only.
 *
 * Web: pass `useLocalStorage`. Mobile: pass an AsyncStorage/MMKV wrapper (or a memory-only hook).
 */
export type PersistedStateHook = <T>(
	key: string | undefined,
	initialValue: T
) => readonly [T | undefined, (value: T) => void, ((...args: never[]) => void)?];

type Props<T extends BaseFilterType> = {
	initialFilters?: Partial<T>;
	requiredFilters?: Partial<T>;
	overrideFilters?: Partial<T>;
	storageKey?: string;
	excludeFilters?: (keyof T)[];
	ignoreFilters?: (keyof T)[];
	usePersistedState: PersistedStateHook;
};

type ReturnType<T extends BaseFilterType> = {
	filters: Partial<T>;
	amountOfActiveFilters: number;
	amountOfActiveFiltersIncludingOverride: number;
	filtersFromStorage?: T;
	applyFilter: (filter: T) => void;
	resetFilter: () => void;
	clearFilters: () => void;
	displayedFilters: string[];
	addFilter: (filterId: string) => void;
	addFilters: (filterIds: string[]) => void;
	removeFilter: (filterId: string) => void;
	removeFilters: (filterIds: string[]) => void;
};

/**
 * Custom hook for managing and applying filters, with injectable persisted storage.
 *
 * @template T - The type of the filter object.
 */
export function useFilterManagement<T extends BaseFilterType>({
	initialFilters = {},
	requiredFilters = {},
	overrideFilters = {},
	ignoreFilters = [],
	storageKey,
	excludeFilters = [],
	usePersistedState,
}: Props<T>): ReturnType<Partial<T>> {
	const [filtersFromStorage, saveFiltersToStorage] = usePersistedState<Partial<T>>(storageKey, {});
	const [displayedFromStorage, saveDisplayedToStorage] = usePersistedState<string[]>(
		storageKey ? `${storageKey}.displayed` : undefined,
		[]
	);

	const initialState = useMemo(() => {
		// When override values (ie URL filters) are present, ignore stored filters to prevent mixing
		// always include the required values
		const hasOverrides =
			Object.keys(overrideFilters).filter(
				(key) => overrideFilters[key] !== null && overrideFilters[key] !== undefined
			).length > 0;
		if (hasOverrides) {
			return {
				...requiredFilters,
				...overrideFilters,
			};
		}
		// then try to read from storage
		if (storageKey && Object.keys(filtersFromStorage || {}).length) {
			return structuredClone({
				...filtersFromStorage,
				...requiredFilters,
			});
		}
		// default is initial + required filters
		return {
			...initialFilters,
			...requiredFilters,
		};
	}, [overrideFilters, initialFilters, storageKey, filtersFromStorage, requiredFilters]);

	const [_storedFilters, _setFilters] = useState<Partial<T>>(initialState || ({} as T));
	const [displayedFilters, setDisplayedFilters] = useState<string[]>(displayedFromStorage ?? []);

	const filters = useMemo(() => {
		return Object.fromEntries(
			Object.entries(_storedFilters).filter(([key]) => !excludeFilters.includes(key as keyof T))
		) as Partial<Partial<T>>;
	}, [_storedFilters, excludeFilters]);

	const amountOfActiveFiltersIncludingOverride = Object.keys(filters || {}).reduce(
		// Check if storage contains any filters, this way we only count the active applied ones
		(acc, key) => (filters?.[key] && !excludeFilters.includes(key) && !ignoreFilters.includes(key) ? acc + 1 : acc),
		0
	);

	const amountOfActiveFiltersExcludingOverrideAndRequired = Object.keys(filters || {}).reduce(
		// Check if storage contains any filters, this way we only count the active applied ones
		(acc, key) =>
			filters?.[key] &&
			!(key in overrideFilters) &&
			!(key in requiredFilters) &&
			!excludeFilters.includes(key) &&
			!ignoreFilters.includes(key)
				? acc + 1
				: acc,
		0
	);
	const hasValidOverrideFilters = useMemo(() => {
		return (
			Object.keys(overrideFilters).filter(
				(key) => overrideFilters[key] !== null && overrideFilters[key] !== undefined
			).length > 0
		);
	}, [overrideFilters]);
	const hasValidRequiredFilters = useMemo(() => {
		return (
			Object.keys(requiredFilters).filter(
				(key) => requiredFilters[key] !== null && requiredFilters[key] !== undefined
			).length > 0
		);
	}, [requiredFilters]);
	const amountOfActiveFilters =
		hasValidOverrideFilters || hasValidRequiredFilters
			? amountOfActiveFiltersExcludingOverrideAndRequired
			: amountOfActiveFiltersIncludingOverride;

	const applyFilter = (filter: Partial<T>) => {
		try {
			// Save the filters to storage if required
			if (storageKey) saveFiltersToStorage(filter);
			// Set the filter in state
			_setFilters({
				...filter,
				...requiredFilters,
			});
		} catch (_) {}
	};

	const resetFilter = () => {
		try {
			const storageIsEmpty = !filtersFromStorage || Object.keys(filtersFromStorage).length === 0;

			_setFilters(
				storageIsEmpty
					? {
							...initialFilters,
							...requiredFilters,
						}
					: { ...filtersFromStorage, ...requiredFilters }
			);
		} catch (_) {}
	};

	const clearFilters = () => {
		applyFilter({});
	};

	const addFilter = (filterId: string) => {
		setDisplayedFilters((prevFilters) => {
			if (prevFilters.includes(filterId)) {
				return prevFilters;
			}
			const updatedFilters = [...prevFilters, filterId];
			saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	};

	const addFilters = (filterIds: string[]) => {
		setDisplayedFilters((prevFilters) => {
			const newFilters = filterIds.filter((filterId) => !prevFilters.includes(filterId));
			if (newFilters.length === 0) {
				return prevFilters;
			}
			const updatedFilters = [...prevFilters, ...newFilters];
			saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	};

	const removeFilter = (filterId: string) => {
		setDisplayedFilters((prevFilters) => {
			const updatedFilters = prevFilters.filter((filter) => filter !== filterId);
			saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	};

	const removeFilters = (filterIds: string[]) => {
		const filterIdsSet = new Set(filterIds);
		setDisplayedFilters((prevFilters) => {
			const updatedFilters = prevFilters.filter((filter) => !filterIdsSet.has(filter));
			saveDisplayedToStorage(updatedFilters);
			return updatedFilters;
		});
	};

	return {
		filters,
		amountOfActiveFilters,
		amountOfActiveFiltersIncludingOverride,
		filtersFromStorage,
		applyFilter,
		resetFilter,
		clearFilters,
		displayedFilters,
		addFilter,
		addFilters,
		removeFilter,
		removeFilters,
	} as const;
}
